import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams, Redirect } from 'react-router-dom';
import permissionTypes from 'constants/permissionTypes';
import PageWrapper from 'components/ui/PageWrapper';
import CancellationForm from './CancellationForm';

import * as Styled from './Cancellation.styled';

const Cancellation = (props) => {
  const {
    getCancellationReasons,
    getAccountInfo,
    getTransactions,
    getPendingPaymentAmount,
    getEstimatedFinalBillDate,
    getOutstandingDsFee,
    getBalanceToSettleWithoutCancelFee,
    cancellationReasons,
    getFacilityByAdfitNo,
    account,
    transactions,
    pendingPaymentAmount,
    localisation,
    cancelAccount,
    estimatedFinalBillDate,
    outstandingDsFee,
    balanceToSettleWithoutCancelFee,
    facility,
    permissions,
  } = props;

  const [isLoaded, setIsLoaded] = useState(false);

  const [wasAccountActiveAtStart, setAccountActiveAtStart] = useState(true);

  const { adfitNo } = useParams();

  const { isActive, name, firstName } = account;

  useEffect(() => {
    (async () => {
      try {
        setIsLoaded(false);

        const restOfPromises = [
          getCancellationReasons(adfitNo),
          getTransactions(adfitNo),
          getPendingPaymentAmount(adfitNo),
          getEstimatedFinalBillDate(adfitNo),
          getOutstandingDsFee(adfitNo),
          getBalanceToSettleWithoutCancelFee(adfitNo),
        ];

        await getAccountInfo(adfitNo);
        const accountDependentPromises = [getFacilityByAdfitNo(adfitNo)];
        await Promise.all([...restOfPromises, ...accountDependentPromises]);
        setIsLoaded(true);
      } catch (e) {
        console.warn(e);
      }
    })();
  }, [
    setIsLoaded,
    adfitNo,
    getCancellationReasons,
    getAccountInfo,
    getTransactions,
    getEstimatedFinalBillDate,
    getOutstandingDsFee,
    getFacilityByAdfitNo,
    getBalanceToSettleWithoutCancelFee,
  ]);

  // Useful to redirect the account if it was not active to begin with.
  useEffect(() => {
    if (!account) return;
    setAccountActiveAtStart(isActive);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);

  if (!isLoaded) {
    return (
      <PageWrapper>
        <Styled.Paper>
          <Styled.PaperHeader>
            <Styled.LoaderHeader />
          </Styled.PaperHeader>
          <Styled.PaperBody>
            <Styled.CancellationLoaderContainer>
              <Styled.CancellationLoader />
            </Styled.CancellationLoaderContainer>
          </Styled.PaperBody>
        </Styled.Paper>
      </PageWrapper>
    );
  }

  //if the account is already cancelled do not show the form.
  if (!isActive && !wasAccountActiveAtStart) {
    return <Redirect to={`/Customers/${adfitNo}/Overview`} />;
  }

  //if the account has no permissions then redirect as well.
  if (
    !permissions.includes(permissionTypes.canSetToTerm) &&
    !permissions.includes(permissionTypes.canCancelAllAccounts)
  ) {
    return <Redirect to={`/Customers/${adfitNo}/Overview`} />;
  }

  return (
    <PageWrapper>
      <Styled.AccountName>{`${
        name || firstName
      }, ${adfitNo}`}</Styled.AccountName>

      <CancellationForm
        cancellationReasons={cancellationReasons}
        account={account}
        localisation={localisation}
        adfitNo={adfitNo}
        cancelAccount={cancelAccount}
        transactions={transactions}
        pendingPaymentAmount={pendingPaymentAmount}
        estimatedFinalBillDate={estimatedFinalBillDate}
        outstandingDsFee={outstandingDsFee}
        balanceToSettleWithoutCancelFee={balanceToSettleWithoutCancelFee}
        facility={facility}
        permissions={permissions}
      />
    </PageWrapper>
  );
};

Cancellation.propTypes = {
  cancellationReasons: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ),
  localisation: PropTypes.object.isRequired,
  account: PropTypes.object,
  estimatedFinalBillDate: PropTypes.string,
  outstandingDsFee: PropTypes.number,
  balanceToSettleWithoutCancelFee: PropTypes.number,
  transactions: PropTypes.arrayOf(
    PropTypes.shape({
      amount: PropTypes.number.isRequired,
      cleared: PropTypes.bool,
    })
  ),
  facility: PropTypes.shape({
    name: PropTypes.string,
    industryType: PropTypes.string.isRequired,
  }),
  getCancellationReasons: PropTypes.func.isRequired,
  getAccountInfo: PropTypes.func.isRequired,
  cancelAccount: PropTypes.func.isRequired,
  getTransactions: PropTypes.func.isRequired,
  getEstimatedFinalBillDate: PropTypes.func.isRequired,
  getOutstandingDsFee: PropTypes.func.isRequired,
  getBalanceToSettleWithoutCancelFee: PropTypes.func.isRequired,
  getFacilityByAdfitNo: PropTypes.func.isRequired,
  permissions: PropTypes.arrayOf(PropTypes.string).isRequired,
};

Cancellation.defaultProps = {
  cancellationReasons: [],
  account: null,
  transactions: [],
  finalPaymentDate: null,
  outstandingDsFee: null,
  balanceToSettle: null,
  balanceToSettleWithoutCancelFee: null,
  estimatedFinalBillDate: '',
};

Cancellation.displayName = 'Cancellation';

export default Cancellation;
